import { HttpBaseService } from "@shared/service/base_services/http_base.service";
import { TermFilterModel } from "@shared/model/categoryAndSearch/term-filter-model";
import { HttpClient } from "@angular/common/http";
import { LocalRepository } from "@shared/helper/local-repository";
import { forkJoin, Observable, of } from "rxjs";
import { ClubDetailResolverData, STATE_KEY_CLUB_DETAIL } from "@app/pages/clubs/resolvers/club-detail.resolver";

import { ClubService } from "@app/pages/clubs/services/club.service";
import { debounceTime, switchMap, catchError } from "rxjs/operators";
import { TServiceResult } from "@shared/helper/t-service-result";
import { ClubAggregatorService } from "@app/pages/clubs/services/club-aggregator.service";
import { ClubMeetingService } from "@app/pages/clubs/services/club-meeting.service";
import { EClubMeetingState } from "@app/pages/clubs/models/club-meeting-model";
import { EClubPollState } from "@app/pages/clubs/models/club-poll-model";
import { Injectable, TransferState, inject } from "@angular/core";
import { EClubCourseStatus } from "@app/pages/clubs/models/club-course-model";
import { ActivitiesAggrigatorService } from "@shared/service/http_services/activitiesAggrigator.service";
import { IClubModel } from "@app/pages/clubs/models/club-model";
import { PageList } from "@shared/model/categoryAndSearch/pageList-model";
import { ActivitiesAggrigatorModel } from "@shared/model/activities-aggrigator-model";

@Injectable({ providedIn: "root" })
export class ClubDetailFacadeService extends HttpBaseService {
  private serverState = inject(TransferState);
  private clubService = inject(ClubService);
  private clubAggregatorService = inject(ClubAggregatorService);
  private clubMeetingService = inject(ClubMeetingService);
  private activitiesAggregatorService = inject(ActivitiesAggrigatorService);

  termFiler = new TermFilterModel();

  constructor() {
    const http = inject(HttpClient);
    const repo = inject(LocalRepository);

    super(repo, http, '');
  }

  resolveClubDetail(clubId: string): Observable<ClubDetailResolverData> {
    const clubResolvedData = this.serverState.get(STATE_KEY_CLUB_DETAIL, ClubDetailResolverData.instanceAllError());
    if (!clubResolvedData.club?.hasError && clubResolvedData?.club?.result?.id == clubId)
      return of(clubResolvedData);

    const error$: Observable<TServiceResult<IClubModel>> = of(new TServiceResult<IClubModel>({}, '', null, true, ''));

    return this.clubService.get(clubId)
      .pipe(
        debounceTime(0),
        switchMap(x =>
          forkJoin({
            club: x ? of(x) : error$,
            courses: this.clubAggregatorService.getClubCourses(clubId, this.termFiler)
          })
        ),
        switchMap(x => {
          const activeCourse = x.courses.result?.items.filter(course => course.status == EClubCourseStatus.InUse)[0];
          const activities$ = activeCourse ?
            this.activitiesAggregatorService.getCourseActivities(activeCourse.id!, this.termFiler) :
            of(new TServiceResult<PageList<ActivitiesAggrigatorModel>>(new PageList(), '', null, true, ''));

          return forkJoin({
            club: of(x.club),
            courses: of(x.courses),
            activeMeeting: this.clubMeetingService.getClubMeetings(clubId, this.termFiler, EClubMeetingState.Pending),
            activePoll: this.clubAggregatorService.getClubPolls(clubId, this.termFiler, EClubPollState.Open),
            activities: activities$
          })
        }),
        switchMap(forkResult => {
          const clubDetailResolverData = ClubDetailResolverData.instance(forkResult);
          this.serverState.set<ClubDetailResolverData>(STATE_KEY_CLUB_DETAIL, clubDetailResolverData);
          return of(clubDetailResolverData);
        }),
        catchError(() => {
          this.serverState.set<ClubDetailResolverData>(STATE_KEY_CLUB_DETAIL, ClubDetailResolverData.instanceAllError());
          return of(ClubDetailResolverData.instanceAllError());
        })
      )
  }
}
